import { activeLevels, formatAmount, getRequiredFields } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useGetCloseoutGlList, useGetCloseoutReport, useGetCommunityFinancialInfo } from "@/services";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { cardTemplates, categoriesMapping, DISTRIBUTION_LEVEL, GL_MASTER_ID, levelOrder, MONTH_RANGE, PERIOD_FROM, PERIOD_TO } from "./consts";
import moment from "moment";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { schema } from "./validation";

const useCloseoutReport = () => {
  const communityData = useSelector((state: RootState) => state.currentData.communityData);
  const currentFinanceYear = useSelector((state: RootState) => state.currentData.currentFinanceYear);
  const communityName = communityData?.name || communityData?.communityName || "";
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [isActual, setIsActual] = useState<boolean>(true);
  const isRunClick = useRef<boolean>(false);
  
  const form = useForm<any>({
    defaultValues: {
      [MONTH_RANGE]: {
        from: "",
        to: ""
      },
      [GL_MASTER_ID]: [],
      [DISTRIBUTION_LEVEL]: "",
    },
    resolver: zodResolver(schema)
  });

  const requiredField = getRequiredFields(schema);
  const formData = useWatch({control: form.control});

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  
  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.bom}/${routes.finance}`,
        name: `Activity Dashboard - ${communityName}`,
      },
      {
        link: `/${routes.bom}/${routes.finance}/${routes.costAnalytics}`,
        name: `Cost Analytics`,
      },
      {
        link: "",
        name: <span className="text-primary">Level Closeout Report</span>,
      },
    ],
    [communityName]
  );

  const { data: glMasterListing, isLoading: isGlMasterLoading } =
    useGetCloseoutGlList({
      communityId: communityData?.communityId as string,
      year: currentFinanceYear,
      type: isActual ? "actuals" : "plan",
      ...(isActual && formData[MONTH_RANGE]?.from && formData[MONTH_RANGE]?.to ? {filter: {
        [PERIOD_FROM]: moment(formData[MONTH_RANGE]?.from).format("YYYY-MM-DD"),
        [PERIOD_TO]: moment(formData[MONTH_RANGE]?.to).format("YYYY-MM-DD"),
        }} : {}),
      queryParams: {
        enabled: isActual ? !!(formData[MONTH_RANGE]?.from && formData[MONTH_RANGE]?.to) : true,
      },
    });
  
  const {data: closeoutReportData, isRefetching: isLoadingReportData, refetch: refetchReportData} = useGetCloseoutReport({
    type: isActual ? "actuals" : "plan",
    communityId: communityData?.communityId,
    year: currentFinanceYear,
    filter: {
      ...(formData[MONTH_RANGE]?.from && formData[MONTH_RANGE]?.to ? {
        [PERIOD_FROM]: moment(formData[MONTH_RANGE]?.from).format("YYYY-MM-DD"),
        [PERIOD_TO]: moment(formData[MONTH_RANGE]?.to).format("YYYY-MM-DD"),
        } : {}
      ),
      ...(formData[GL_MASTER_ID]?.length && !formData[GL_MASTER_ID]?.includes("all") ? {[GL_MASTER_ID]: formData[GL_MASTER_ID]} : {}),
      ...(formData[DISTRIBUTION_LEVEL] ? {[DISTRIBUTION_LEVEL]: formData[DISTRIBUTION_LEVEL]} : {}),
    },
    queryParams: {
      enabled: false
    }
  })

  const {data: financialInfo, isLoading: isLoadingFinancialInfo } = useGetCommunityFinancialInfo({
    community_id: communityData?.communityId,
    queryParams: {
      enabled: !!(communityData?.communityId)
    }
  })
  
  // this is to display level in distribution level dropdown
  const activeThresholdIndex = useMemo(() => {
    return activeLevels.findIndex(
      (level) => level.value == financialInfo?.data?.distributionLevel
    );
  }, [financialInfo])
  // --------------------------------------------------------------
  
  // ------------- this is for displaying level in cards 
  const distributionLevel = useMemo(() => closeoutReportData?.[DISTRIBUTION_LEVEL], [closeoutReportData]);
  const currentLevelIndex = levelOrder.indexOf(distributionLevel);
  // ---------------------------------------------------------------

  const summaryCards = useMemo(() => { 
    if (!closeoutReportData) return [...cardTemplates];

    const structuralCards = cardTemplates.map((card) => {
      const totalValue = closeoutReportData[card.totalKey as keyof typeof closeoutReportData] || 0;
      const descValue = closeoutReportData[card.descKey as keyof typeof closeoutReportData] || 0;
  
      return {
        ...card,
        id: card.id,
        title: card.title,
        total: formatAmount(String(totalValue)),
        desc: descValue ? `${descValue} Open` : "Recorded",
        hasDescValue: !!descValue
      };
    });
  
    const visibleCards = structuralCards?.filter((card: any) => {
      const cardLevelIndex = levelOrder.indexOf(card?.id);
      return cardLevelIndex !== -1 && currentLevelIndex !== -1
        ? cardLevelIndex <= currentLevelIndex
        : true;
    });

    return [
      ...visibleCards
    ];
  }, [closeoutReportData, cardTemplates]);

  const onSubmit = () => {
    isRunClick.current = true;
    refetchReportData()
  }

  const onClear = () => {
    form.reset({
      [MONTH_RANGE]: {
        from: "",
        to: ""
      },
      [GL_MASTER_ID]: [],
      [DISTRIBUTION_LEVEL]: "",
    })
    isRunClick.current = false;
  }
  
  const closeoutTableData = useMemo(() => {
    if (!closeoutReportData?.categories) return [];

    const data = closeoutReportData?.categories?.map((cat: any) => ({
      ...cat,
      label: (categoriesMapping as any)[cat?.category] || "",
      communityTotal: cat?.categoryTotal,
      children: (cat?.glRows || []).map((gl: any) => ({
        ...gl,
        label: gl?.glAccountNumber || "",
        description: gl?.description,
        children: (gl?.zones || []).map((zone: any) => ({
          ...zone,
          label: zone?.zoneName,
          zoneTotal: zone?.total,
          zoneVariance: zone?.variance,
          systemNote: zone?.variance ? "Check Variance" : "Ok",
          rule: zone?.zoneRule || "-",
          children: (zone?.blocks || []).map((block: any) => ({
            ...block,
            label: block?.blockName,
            blockTotal: block?.total,
            blockVariance: block?.variance,
            systemNote: block?.variance ? "Check Variance" : "Ok",
            rule: block?.blockRule || "-",
            children: (block?.properties || []).map((prop: any) => ({
              ...prop,
              label: prop?.propertyName,
              propertyTotal: prop?.total,
              propertyVariance: prop?.variance,
              systemNote: prop?.variance ? "Check Variance" : "Ok",
              rule: prop?.propertyRule || "-",
            })),
          })),
        })),
      })),
    }));
  
    return data;
  }, [closeoutReportData]);

  const onChangeType = (checked: boolean) => {
    setIsActual(checked);
    setTimeout(() => refetchReportData(), 100);
  }

  return {
    breadcrumbData,
    isLoadingReportData,
    summaryCards,
    globalFilter,
    setGlobalFilter,
    isActual,
    setIsActual,
    form,
    glMasterListing,
    isGlMasterLoading,
    onClear,
    closeoutTableData,
    onSubmit,
    isRunClick: isRunClick.current,
    formData,
    currentFinanceYear,
    requiredField,
    onChangeType,
    closeoutReportData,
    distributionLevel,
    isLoadingFinancialInfo,
    activeThresholdIndex
  }
}

export default useCloseoutReport;
